/**
 * Central utility for formatting chart labels from entities
 */

import { GROUP_OPTIONS } from "../modals/chartCreationConstants";

/**
 * Get the property name to use for a given label type
 * @param {string} labelType - The type of label (assignee, creator, project, etc.)
 * @returns {string} - The property name to extract from entity
 */
const getLabelProperty = (labelType) => {
  const propertyMap = {
    assignee: 'fullName',
    creator: 'fullName',
    project: 'title',
    portfolio: 'name',
    tag: 'tag',
    completion: 'label',
    priority: 'label',
    status: 'label',
    customField: 'label',
  };

  if (labelType?.startsWith(GROUP_OPTIONS.CUSTOM_FIELD_PREFIX)) return 'label';

  return propertyMap[labelType] || 'label';
};

/**
 * Format a label using entities
 * @param {string} value - The raw value (often an ID)
 * @param {string} labelType - The type of label (assignee, creator, project, etc.)
 * @param {Object} entities - The entities object containing the mapping
 * @returns {string} - The formatted label
 */
export const formatLabel = (value, labelType, entities) => {
  if (!value || value === 'unassigned' || value === 'undefined') {
    return 'Unassigned';
  }

  if (value === 'novalue') {
    return '--';
  }

  if (!entities || !labelType) {
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  }

  const entity = entities[value];
  if (!entity) {
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  }

  // Try the preferred property for this label type first
  const property = getLabelProperty(labelType);
  if (entity[property]) return entity[property];

  // Fallback chain — handles customField entities that may be user/project/tag objects
  return (
    entity.fullName ||
    entity.title ||
    entity.tag ||
    entity.name ||
    entity.label ||
    String(value)
  );
};

/**
 * Format multiple labels at once (useful for stacked charts)
 * @param {Array} values - Array of values to format
 * @param {string} labelType - The type of label
 * @param {Object} entities - The entities object
 * @returns {Array} - Array of formatted labels
 */
export const formatLabels = (values, labelType, entities) => {
  return values.map((value) => formatLabel(value, labelType, entities));
};

/**
 * Create a formatter function bound to specific entities and type
 * Useful for passing to recharts components
 * @param {string} labelType - The type of label
 * @param {Object} entities - The entities object
 * @returns {Function} - A function that takes a value and returns formatted label
 */
export const createLabelFormatter = (labelType, entities) => {
  return (value) => formatLabel(value, labelType, entities);
};
